import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { BrandDto, BrandInput } from '@rw/shared';
import { useState } from 'react';
import { Link } from 'react-router';
import { Sheet } from '../../components/Sheet';
import { LoadingState } from '../../components/states';
import { Button, Card } from '../../components/ui';
import { ApiError, api } from '../../lib/api';
import { fittedLogo } from '../../lib/resize-image';
import { t as tr, useT } from '../../i18n';
import { toast } from '../../stores/toasts';
import { brandKeys, useBrands } from './brands-api';


const input = 'mt-1 h-11 w-full rounded-xl border border-ink-700 bg-ink-850 px-3 outline-none focus:border-volt';

/** Game owner: brands (event sponsors, later advertisers): add, edit, logo, archive. */
export function BrandsTab() {
  const q = useBrands();
  const qc = useQueryClient();
  const t = useT();
  const [editing, setEditing] = useState<BrandDto | 'new' | null>(null);
  const archive = useMutation({
    mutationFn: (v: { id: string; archived: boolean }) => api<BrandDto>(`/admin/brands/${v.id}/archive`, { method: 'POST', body: { archived: v.archived } }),
    onSuccess: (b) => {
      void qc.invalidateQueries({ queryKey: brandKeys.all });
      toast({ kind: 'success', title: tr(b.archived ? '{name} archived' : '{name} restored', { name: b.name }) });
    },
  });
  if (!q.data) return <LoadingState />;
  return (
    <div className="mt-4 space-y-3" data-testid="admin-brands">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-ink-400">{t('Sponsors and advertisers. Archived brands stay on events they sponsored.')}</p>
        <Button size="sm" className="shrink-0 whitespace-nowrap" onClick={() => setEditing('new')} data-testid="brand-new">
          {t('+ Brand')}
        </Button>
      </div>
      {q.data.length === 0 ? <p className="text-sm text-ink-500">{t('No brands yet.')}</p> : null}
      {q.data.map((b) => (
        <Card key={b.id} className={`flex items-center gap-3 ${b.archived ? 'opacity-60' : ''}`}>
          <BrandLogo brand={b} className="h-12 w-20" />
          <div className="min-w-0 flex-1">
            <div className="truncate font-semibold" data-testid="brand-row-name">
              {b.name}
            </div>
            <div className="truncate text-xs text-ink-400">
              {t(b.events === 1 ? '{n} event' : '{n} events', { n: b.events })}
              {b.archived ? ` · ${t('archived')}` : ''}
              {b.website ? ` · ${b.website.replace(/^https?:\/\//, '')}` : ''}
            </div>
          </div>
          <Link to={`/admin/reports/${b.id}`} className="text-xs font-semibold text-volt" data-testid={`brand-report-${b.name}`}>
            {t('Report')}
          </Link>
          {b.archived ? (
            <Button size="sm" variant="secondary" onClick={() => archive.mutate({ id: b.id, archived: false })}>
              {t('Restore')}
            </Button>
          ) : (
            <Button size="sm" variant="secondary" onClick={() => setEditing(b)} data-testid={`brand-edit-${b.name}`}>
              {t('Edit')}
            </Button>
          )}
        </Card>
      ))}
      <BrandSheet brand={editing} onClose={() => setEditing(null)} onArchive={(b) => archive.mutate({ id: b.id, archived: true })} />
    </div>
  );
}

export function BrandLogo({ brand, className = '' }: { brand: { name: string; logoUrl: string | null }; className?: string }) {
  return brand.logoUrl ? (
    <img src={brand.logoUrl} alt={brand.name} className={`shrink-0 rounded-lg border border-ink-700 bg-ink-850 object-contain p-1 ${className}`} />
  ) : (
    <span className={`grid shrink-0 place-items-center rounded-lg bg-ink-700 px-1 text-center text-xs font-bold text-ink-200 ${className}`}>{brand.name}</span>
  );
}

function BrandSheet({ brand, onClose, onArchive }: { brand: BrandDto | 'new' | null; onClose: () => void; onArchive: (b: BrandDto) => void }) {
  const qc = useQueryClient();
  const t = useT();
  const existing = brand && brand !== 'new' ? brand : null;
  const [form, setForm] = useState<Record<string, string>>({});
  const [logo, setLogo] = useState<string | null>(null);
  const key = existing?.id ?? 'new';
  const [formKey, setFormKey] = useState<string | null>(null);
  if (brand && formKey !== key) {
    setFormKey(key);
    setForm({
      name: existing?.name ?? '',
      website: existing?.website ?? '',
      contactName: existing?.contactName ?? '',
      contactPhone: existing?.contactPhone ?? '',
      contactEmail: existing?.contactEmail ?? '',
      notes: existing?.notes ?? '',
      color: existing?.color ?? '#f59e0b',
    });
    setLogo(null);
  }
  const close = () => {
    setFormKey(null);
    onClose();
  };
  const save = useMutation({
    mutationFn: async () => {
      const blank = (v: string | undefined) => (v && v.trim() ? v.trim() : null);
      const body: BrandInput = {
        name: form.name ?? '',
        website: blank(form.website),
        contactName: blank(form.contactName),
        contactPhone: blank(form.contactPhone),
        contactEmail: blank(form.contactEmail),
        notes: form.notes ?? '',
        color: form.color,
      };
      const saved = existing
        ? await api<BrandDto>(`/admin/brands/${existing.id}`, { method: 'PATCH', body })
        : await api<BrandDto>('/admin/brands', { body });
      return logo ? api<BrandDto>(`/admin/brands/${saved.id}/logo`, { method: 'PUT', body: { image: logo } }) : saved;
    },
    onSuccess: (b) => {
      void qc.invalidateQueries({ queryKey: brandKeys.all });
      toast({ kind: 'success', title: tr('{name} saved', { name: b.name }) });
      close();
    },
    onError: (e) => toast({ kind: 'error', title: e instanceof ApiError ? e.message : tr('Could not save the brand') }),
  });
  const field = (k: string, label: string, type = 'text') => (
    <label className="block text-sm font-semibold">
      {label}
      <input type={type} value={form[k] ?? ''} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className={input} data-testid={`brand-${k}`} />
    </label>
  );
  return (
    <Sheet open={!!brand} onClose={close} label={t('Brand')}>
      {brand ? (
        <div className="space-y-3" data-testid="brand-sheet">
          <h2 className="font-display text-2xl font-bold">{existing ? t('Edit {name}', { name: existing.name }) : t('New brand')}</h2>
          <div className="flex items-center gap-3">
            <BrandLogo brand={{ name: form.name || '?', logoUrl: logo ?? existing?.logoUrl ?? null }} className="h-14 w-24" />
            <label className="cursor-pointer text-sm font-semibold text-volt">
              {t(logo || existing?.logoUrl ? 'Change logo' : 'Upload logo (PNG, transparent background)')}
              <input
                type="file"
                accept="image/*"
                className="hidden"
                data-testid="brand-logo-input"
                onChange={async (e) => {
                  const f = e.target.files?.[0];
                  if (f) setLogo(await fittedLogo(f).catch(() => null));
                }}
              />
            </label>
          </div>
          {field('name', t('Name'))}
          {field('website', t('Website (https://…)'), 'url')}
          <div className="grid grid-cols-2 gap-3">
            {field('contactName', t('Contact'))}
            {field('contactPhone', t('Phone'), 'tel')}
          </div>
          {field('contactEmail', t('Email'), 'email')}
          <label className="block text-sm font-semibold">
            {t('Notes (only you see them)')}
            <textarea value={form.notes ?? ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className={`${input} h-auto py-2`} />
          </label>
          <label className="flex items-center gap-3 text-sm font-semibold">
            {t('Brand colour')}
            <input type="color" value={form.color ?? '#f59e0b'} onChange={(e) => setForm({ ...form, color: e.target.value })} className="h-9 w-14 rounded" />
          </label>
          <div className="flex gap-2 pt-2">
            {existing ? (
              <Button variant="secondary" onClick={() => (onArchive(existing), close())} data-testid="brand-archive">
                {t('Delete')}
              </Button>
            ) : null}
            <Button className="flex-1" loading={save.isPending} disabled={(form.name ?? '').trim().length < 2} onClick={() => save.mutate()} data-testid="brand-save">
              {t('Save')}
            </Button>
          </div>
        </div>
      ) : null}
    </Sheet>
  );
}
